import { Cart } from '@diamond/shared/types';
import { trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { useCallback, useEffect, useRef } from 'react';

import {
  buildRemoveItemPayload,
  buildViewCartPayload,
  calculateCartTotal,
} from '../../cart';

export interface UseCartTrackingReturn {
  trackViewCart: (items: Cart[]) => void;
  trackRemoveItem: (item: Cart) => void;
  trackRemoveAll: (items: Cart[]) => void;
  trackBeginCheckout: (
    checkedItems: Cart[],
    transactionId: string,
    warehouseId: string
  ) => void;
}

export const useCartTracking = (): UseCartTrackingReturn => {
  const hasTrackedViewCart = useRef<boolean>(false);

  const trackViewCart = useCallback((items: Cart[]) => {
    if (!items.length || hasTrackedViewCart.current) return;

    const payload = buildViewCartPayload(items);
    trackEvent(TRACKING_EVENTS.VIEW_CART, payload);
    hasTrackedViewCart.current = true;
  }, []);

  const trackRemoveItem = useCallback((item: Cart) => {
    const payload = buildRemoveItemPayload(item);
    trackEvent(TRACKING_EVENTS.REMOVE_FROM_CART, payload);
  }, []);

  const trackRemoveAll = useCallback((items: Cart[]) => {
    const totalValue = calculateCartTotal(items);
    trackEvent(TRACKING_EVENTS.REMOVE_FROM_CART, {
      currency: 'IDR',
      value: totalValue,
      action_type: 'remove_all',
      count_items: items.length,
      section_name: TRACKING_PAGES.CART,
    });
  }, []);

  const trackBeginCheckout = useCallback(
    (checkedItems: Cart[], transactionId: string, warehouseId: string) => {
      const totalValue = calculateCartTotal(checkedItems);
      const ecommerceItems = checkedItems.map((item, index) => {
        const finalPrice = item.product.prices?.[0]?.price || 0;
        return {
          id: item.id,
          item_id: item.product.item_code?.toString(),
          item_name: item.product.title,
          item_brand: item.product.brand_name,
          affiliation: item.product.brand_desc,
          index,
          item_category: item.product.category_name,
          item_category2: item.product.sub_category_name,
          item_list_id: 'cart',
          item_list_name: 'Cart',
          item_variant: item.product.type || '',
          location_id: warehouseId,
          price: finalPrice,
          quantity: item.quantity / 1000 || 1,
        };
      });

      trackEvent(TRACKING_EVENTS.BEGIN_CHECKOUT, {
        currency: 'IDR',
        value: totalValue,
        transaction_id: transactionId,
        items: ecommerceItems,
        section_name: TRACKING_PAGES.CART,
        page_name: TRACKING_PAGES.CART,
      });
    },
    []
  );

  return {
    trackViewCart,
    trackRemoveItem,
    trackRemoveAll,
    trackBeginCheckout,
  };
};
